import gql from 'graphql-tag'

/**
 * GraphQL fragment with every PAMM offer field the hierarchical tree view needs.
 *
 * Shared by both the Money Manager (`pammOffers`) and Agent (`pammAgentOffers`) queries
 * so the two pages produce an identical `PammOfferType` shape for `PammOffersTree`.
 */
export const PAMM_OFFER_TREE_FIELDS = gql`
  fragment PammOfferTreeFields on PammOfferType {
    id
    name
    # totalInvestments is a server-computed investor count and is populated for both
    # Money Manager and Agent offers; activeInvestmentCount is only set on Agent offers.
    totalInvestments
    currency
    managerId
    managerName
    # The offer the first agent of the chain joined the manager through as an investor: a manager's
    # own offer or the offer of another first-level agent (PT-6731). The tree hangs a first-level
    # agent offer under it. Null when that agent never
    # invested with the manager, and on a manager's own offer. Set only for a chain of length 1.
    agentJoinedOfferId
    totalDepositSum
    # KPI grid figures (PT-5711), precomputed server-side and delivered with the tree so the tiles
    # read them with no extra call: aum = current assets under management (sum of investor balances,
    # nets out withdrawals and P/L), lifetimeWithdrawals = lifetime successful investor withdrawals.
    aum
    lifetimeWithdrawals
    # Combined PAMM fee ("AllFees") collected from the offer over the trailing 30 days (PT-5784),
    # precomputed in the same grouped pass as aum. Feeds the "PF Collected" KPI tile.
    pfCollected30d
    # Profit/loss the offer's investors realised on positions closed over the trailing 30 days
    # (PT-5809), from the same grouped pass. Signed — negative on a losing month. Feeds "Node PnL".
    nodePnl30d
    # How the offer's Money Manager traded over the trailing 30 days (PT-5815). Per manager, not per
    # offer — every offer of one manager carries the same figures. Feeds the "MM Trades" tile.
    mmTrades30d {
      closed
      winRate
      open
    }
    skygroundAccountId
    # The investor book is fetched with the tree rather than on popover open: PT-5707 fixes the
    # open time at under 100 ms for 500 rows, which a request issued on click cannot promise.
    # The server only ever returns this for nodes the viewer owns (PT-5708).
    investors {
      id
      name
      funds
      share
      created
      # Masked server-side to j***@domain for an upstream agent who does not own this node;
      # emailMasked says which it is, so the row knows not to offer it as a mailto (PT-5709).
      email
      emailMasked
    }
    settings {
      agentChain {
        agentId
        client {
          id
        }
      }
      tradingInterval {
        type
        count
      }
      minDeposit
      minInitialInvestment
      minWithdrawal
      performanceFees {
        levels {
          level
          value
        }
        mode
      }
    }
  }
`
